import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, addDoc, collection, serverTimestamp, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { FaShare, FaPlus, FaEllipsisH, FaChevronLeft, FaUserCheck } from 'react-icons/fa';
import { GoHeartFill } from "react-icons/go";
import { BiSolidMessageSquareDetail } from "react-icons/bi";
import { IoBookmark } from "react-icons/io5";
import { useStatusContext } from '../utils/StatusContext';
import { useFollow } from '../utils/FollowContext';
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';
import VerificationBadge from '../components/VerificationBadge';
import { toast } from 'react-toastify';
import CommentSection from '../components/CommentSection';
import Modal from 'react-modal';

Modal.setAppElement('#root');

export default function FullStatusPostItem({ status }) {
  const auth = getAuth();
  const navigate = useNavigate();
  const { viewedStatuses, setViewedStatuses } = useStatusContext();
  const {
    isFollowing,
    shouldFollowBack,
    toggleFollow,
    followers,
    isInitialized
  } = useFollow();
  const [userData, setUserData] = useState({
    profileImage: defaultAvatar,
    name: 'Unknown',
    username: 'unknown',
    followersCount: 0,
    verified: false
  });
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showFullText, setShowFullText] = useState(false);
  const [showUIElements, setShowUIElements] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [likeAnimation, setLikeAnimation] = useState(false);
  const [saveAnimation, setSaveAnimation] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [commentCount, setCommentCount] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const hasViewed = viewedStatuses.has(status.id);

  const timeSincePost = () => {
    const postTime = status.timestamp?.toDate();
    if (!postTime) return 'Just now';
    
    const now = new Date();
    const diffSeconds = Math.floor((now - postTime) / 1000);
    
    if (diffSeconds < 60) return 'Just now';
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h`;
    return `${Math.floor(diffSeconds / 86400)}d`;
  };

  const toggleUIElements = () => {
    setShowUIElements(!showUIElements);
  };

  const navigateImage = (direction) => {
    if (!status.imageUrls) return;
    
    if (direction === 'prev') {
      setCurrentImageIndex(prev => 
        prev === 0 ? status.imageUrls.length - 1 : prev - 1
      );
    } else {
      setCurrentImageIndex(prev => 
        prev === status.imageUrls.length - 1 ? 0 : prev + 1
      );
    }
  };

  useEffect(() => {
    async function fetchUserData() {
      try {
        const userRef = doc(db, 'users', status.userRef);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData({
            profileImage: data.photoURL || defaultAvatar,
            name: data.name || 'Unknown',
            username: data.username || 'unknown',
            followersCount: data.followers?.length || 0,
            verified: data.verified || false
          });

          if (auth.currentUser) {
            setIsLiked(status.likedBy?.includes(auth.currentUser.uid));
            setIsSaved(status.savedBy?.includes(auth.currentUser.uid));
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
    fetchUserData();
  }, [status.userRef, status.likedBy, status.savedBy, auth.currentUser, followers]);

  useEffect(() => {
    async function fetchCurrentUserData() {
      if (!auth.currentUser) return;

      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setCurrentUserData(userSnap.data());
        }
      } catch (error) {
        console.error('Error fetching current user data:', error);
      }
    }
    fetchCurrentUserData();
  }, [auth.currentUser]);

  useEffect(() => {
    async function markAsViewed() {
      if (!auth.currentUser || hasViewed) return;

      try {
        const statusRef = doc(db, 'status', status.id);
        await updateDoc(statusRef, {
          viewedBy: arrayUnion(auth.currentUser.uid)
        });
        setViewedStatuses(prev => new Set([...prev, status.id]));
      } catch (error) {
        console.error('Error marking status as viewed:', error);
      }
    }
    markAsViewed();
  }, [auth.currentUser, hasViewed, status.id, setViewedStatuses]);

  useEffect(() => {
    if (!status.id) return;

    const commentsRef = collection(db, `status/${status.id}/comments`);
    const q = query(commentsRef, orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setCommentCount(snapshot.size);
    }, (error) => {
      console.error("Error fetching comment count:", error);
    });

    return () => unsubscribe();
  }, [status.id]);

  const createNotification = async (type, recipientId, message, targetId = null) => {
    if (!auth.currentUser || auth.currentUser.uid === recipientId) return;

    try {
      const notifRef = collection(db, `notifications/${recipientId}/userNotifications`);
      await addDoc(notifRef, {
        type,
        senderId: auth.currentUser.uid,
        targetId,
        message,
        timestamp: serverTimestamp(),
        isRead: false
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const handleFollow = async (e) => {
    e.stopPropagation();
    if (!auth.currentUser) {
      navigate('/sign-in');
      return;
    }

    setIsFollowLoading(true);
    try {
      const wasFollowing = isFollowing(status.userRef);
      await toggleFollow(status.userRef);
      if (!wasFollowing) {
        const message = `started following you.`;
        await createNotification('follow', status.userRef, message);
      }
    } catch (error) {
      console.error('Error handling follow:', error);
    } finally {
      setIsFollowLoading(false);
    }
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!auth.currentUser) {
      navigate('/sign-in');
      return;
    }

    try {
      setLikeAnimation(true);
      setTimeout(() => setLikeAnimation(false), 1000);
      
      const statusRef = doc(db, 'status', status.id);
      if (isLiked) {
        await updateDoc(statusRef, {
          likedBy: arrayRemove(auth.currentUser.uid)
        });
        setIsLiked(false);
      } else {
        await updateDoc(statusRef, {
          likedBy: arrayUnion(auth.currentUser.uid)
        });
        setIsLiked(true);
        const statusExcerpt = status.text ? status.text.slice(0, 50) + (status.text.length > 50 ? '...' : '') : 'No caption';
        const message = `liked your status: ${statusExcerpt}.`;
        await createNotification('like', status.userRef, message, status.id);
      }
    } catch (error) {
      console.error('Error handling like:', error);
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!auth.currentUser) {
      navigate('/sign-in');
      return;
    }

    try {
      setSaveAnimation(true);
      setTimeout(() => setSaveAnimation(false), 1000);
      
      const statusRef = doc(db, 'status', status.id);
      if (isSaved) {
        await updateDoc(statusRef, {
          savedBy: arrayRemove(auth.currentUser.uid)
        });
        setIsSaved(false);
      } else {
        await updateDoc(statusRef, {
          savedBy: arrayUnion(auth.currentUser.uid)
        });
        setIsSaved(true);
        const statusExcerpt = status.text ? status.text.slice(0, 50) + (status.text.length > 50 ? '...' : '') : 'No caption';
        const message = `saved your status: ${statusExcerpt}.`;
        await createNotification('save', status.userRef, message, status.id);
      }
    } catch (error) {
      console.error('Error handling save:', error);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    
    const shareUrl = `${window.location.origin}/status/${status.id}`;
    const shareData = {
      title: `${userData.name}'s Status`,
      text: `${userData.name} shared: ${status.text ? status.text.slice(0, 50) + (status.text.length > 50 ? '...' : '') : 'Check out this status!'}`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.info('Status link copied to clipboard! Share it manually.');
      }
    } catch (error) {
      console.error('Error sharing status:', error);
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.info('Status link copied to clipboard! Share it manually.');
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError);
        toast.error('Failed to share status. Please share the link manually.');
      }
    }
  };

  const handleComment = (e) => {
    e.stopPropagation();
    if (!auth.currentUser) {
      toast.error("Please sign in to comment");
      return;
    }
    setShowComments(!showComments);
  };

  function handleProfileClick(e) {
    e.stopPropagation();
    navigate(`/profile/${status.userRef}`);
  }

  const formatCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const getFollowButtonState = () => {
    if (!isInitialized) {
      return {
        state: 'loading',
        className: 'bg-gray-500',
        icon: '...',
        disabled: true
      };
    }
    
    if (isFollowing(status.userRef)) {
      return {
        state: 'following',
        className: 'bg-black hover:bg-gray-800',
        icon: <FaUserCheck size={16} />,
        disabled: false
      };
    }
    if (shouldFollowBack(status.userRef)) {
      return {
        state: 'follow-back',
        className: 'bg-pink-500 hover:bg-pink-600',
        icon: <FaPlus size={16} />,
        disabled: false
      };
    }
    return {
      state: 'follow',
      className: 'bg-pink-500 hover:bg-pink-600',
      icon: <FaPlus size={16} />,
      disabled: false
    };
  };

  const followState = getFollowButtonState();

  if (!isInitialized) {
    return (
      <div className="relative w-full h-screen bg-black text-white flex items-center justify-center snap-start">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full h-full bg-gray-800 animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative w-full h-screen bg-black text-white flex items-center justify-center snap-start"
      onClick={toggleUIElements}
    >
      <style jsx>{`
        @keyframes likeBounce {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.2); }
          50% { transform: scale(0.9); }
          75% { transform: scale(1.1); }
        }

        @keyframes saveBounce {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.2) rotate(-10deg); }
          50% { transform: scale(0.9); }
          75% { transform: scale(1.1) rotate(10deg); }
        }
      `}</style>

      {/* Centered Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        {status.imageUrls?.length > 0 ? (
          <div className="w-full h-full relative">
            {/* Desktop view with horizontal scroll and navigation */}
            <div className="hidden md:flex w-full h-full overflow-hidden relative">
              <div 
                className="flex transition-transform duration-300 ease-out"
                style={{
                  transform: `translateX(-${currentImageIndex * 100}%)`,
                  width: `${status.imageUrls.length * 100}%`
                }}
              >
                {status.imageUrls.map((url, index) => (
                  <div key={index} className="w-full h-full flex-shrink-0 flex items-center justify-center">
                    <img
                      src={url}
                      alt={`Status image ${index + 1}`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ))}
              </div>
              
              {/* Left navigation button */}
              {status.imageUrls.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateImage('prev');
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full z-20 hover:bg-black/70"
                >
                  <FaChevronLeft size={24} />
                </button>
              )}
            </div>
            
            {/* Mobile view with scrollable original size images */}
            <div className="md:hidden w-full h-full flex overflow-x-auto snap-x snap-mandatory">
              {status.imageUrls.map((url, index) => (
                <div key={index} className="w-full h-full flex-shrink-0 snap-center flex items-center justify-center">
                  <img
                    src={url}
                    alt={`Status image ${index + 1}`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          status.text && (
            <div className="w-full h-full flex flex-col items-center justify-center p-4">
              <div className="w-full max-w-md mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProfileClick(e);
                      }}
                      className="text-white font-bold text-lg hover:underline inline-flex items-center"
                    >
                      {userData.name}
                      <VerificationBadge isVerified={userData.verified} />
                    </button>
                    <button className="text-gray-300 hover:text-white">
                      <FaEllipsisH size={16} />
                    </button>
                  </div>
                  <span className="text-gray-400 text-sm bg-black/30 px-2 py-1 rounded-full">
                    {timeSincePost()}
                  </span>
                </div>
              </div>
              <p className="text-white text-lg text-center leading-7">
                {status.text}
              </p>
            </div>
          )
        )}
      </div>

      {/* Right Side Action Buttons */}
      {showUIElements && (
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex flex-col items-center space-y-6 z-10">
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleProfileClick(e);
              }}
              className="w-14 h-14 rounded-full overflow-hidden focus:outline-none border-2 border-white/20"
            >
              <img
                src={userData.profileImage}
                alt={`${userData.name}'s profile`}
                className="w-full h-full object-cover"
                onError={(e) => (e.target.src = defaultAvatar)}
              />
            </button>
            {auth.currentUser?.uid !== status.userRef && (
              <button
                onClick={handleFollow}
                disabled={isFollowLoading || followState.disabled}
                className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center text-white transition duration-300 focus:outline-none ${
                  followState.className
                } ${isFollowLoading ? 'opacity-70' : ''}`}
              >
                {isFollowLoading ? '...' : followState.icon}
              </button>
            )}
          </div>

          <div className="flex flex-col items-center space-y-4">
            {/* Like Button */}
            <div className="flex flex-col items-center">
              <button
                onClick={handleLike}
                className={`p-2 rounded-full bg-black/30 backdrop-blur-sm hover:text-red-400 transition duration-300 focus:outline-none ${
                  isLiked ? 'text-red-500' : 'text-white'
                }`}
              >
                <GoHeartFill 
                  size={28} 
                  className={likeAnimation ? "animate-[likeBounce_1s_ease-in-out]" : ""} 
                />
              </button>
              <p className="text-white text-sm mt-1 font-medium drop-shadow-lg">
                {formatCount(status.likedBy?.length || 0)}
              </p>
            </div>

            {/* Comment Button */}
            <div className="flex flex-col items-center">
              <button
                onClick={handleComment}
                className={`p-2 rounded-full bg-black/30 backdrop-blur-sm text-white hover:text-white transition duration-300 focus:outline-none ${
                  showComments ? 'text-pink-500' : 'text-white'
                }`}
              >
                <BiSolidMessageSquareDetail size={28} />
              </button>
              <p className="text-white text-sm mt-1 font-medium drop-shadow-lg">
                {formatCount(commentCount)}
              </p>
            </div>

            {/* Save Button */}
            <div className="flex flex-col items-center">
              <button
                onClick={handleSave}
                className={`p-2 rounded-full bg-black/30 backdrop-blur-sm hover:text-yellow-400 transition duration-300 focus:outline-none ${
                  isSaved ? 'text-yellow-500' : 'text-white'
                }`}
              >
                <IoBookmark 
                  size={28} 
                  className={saveAnimation ? "animate-[saveBounce_1s_ease-in-out]" : ""} 
                />
              </button>
              <p className="text-white text-sm mt-1 font-medium drop-shadow-lg">
                {formatCount(status.savedBy?.length || 0)}
              </p>
            </div>

            {/* Share Button */}
            <div className="flex flex-col items-center">
              <button
                onClick={handleShare}
                className="p-2 rounded-full bg-black/30 backdrop-blur-sm text-white hover:text-white transition duration-300 focus:outline-none"
              >
                <FaShare size={28} />
              </button>
              <p className="text-white text-sm mt-1 font-medium drop-shadow-lg">
                {"Share"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Content */}
      {showUIElements && (
        <div className={`absolute bottom-4 left-4 right-4 z-10 ${status.imageUrls?.length > 0 ? '' : 'hidden'}`}>
          <div className="flex items-center mb-2">
            <div className="flex items-center gap-2">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleProfileClick(e);
                }}
                className="text-white font-bold text-lg hover:underline inline-flex items-center"
              >
                {userData.name}
                <VerificationBadge isVerified={userData.verified} />
              </button>
              <button className="text-gray-300 hover:text-white">
                <FaEllipsisH size={16} />
              </button>
            </div>
            <span className="text-gray-300 text-sm bg-black/30 px-2 py-1 rounded-full ml-2">
              {timeSincePost()}
            </span>
          </div>

          {status.text && (
            <div className="bg-black/30 backdrop-blur-sm p-3 rounded-lg">
              <p className={`text-white text-base leading-6 ${showFullText ? '' : 'line-clamp-2'}`}>
                {status.text}
              </p>
              {status.text.length > 80 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFullText(!showFullText);
                  }}
                  className="text-gray-300 text-sm hover:text-white focus:outline-none mt-1"
                >
                  {showFullText ? 'Hide' : 'See more'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Comment Section in Modal */}
      <Modal
        isOpen={showComments}
        onRequestClose={() => setShowComments(false)}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#1a1a1a',
            border: 'none',
            borderRadius: '8px',
            padding: '20px',
            maxHeight: '80vh',
            overflowY: 'auto',
            width: '90%',
            maxWidth: '500px',
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 1000,
          },
        }}
        contentLabel="Comment Modal"
      >
        <CommentSection contentId={status.id} contentType="status" />
        <button
          onClick={() => setShowComments(false)}
          className="mt-4 px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
        >
          Close
        </button>
      </Modal>
    </div>
  );
}